import { planAllows, type PlanFeature } from '@keel/core';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useTRPC } from '@/api/query';
import { useSession } from './session';

export interface EntityFeatures {
  readonly cgt2027: boolean;
  readonly franking: boolean;
  readonly gstBas: boolean;
  readonly superannuation: boolean;
  readonly vatReturn: boolean;
}

/** Same rule as the API's `featuresFor`, used until `entities.get` answers (and for entities not loaded yet). */
export const featuresForCountry = (country: string | undefined): EntityFeatures => ({
  cgt2027: country === 'AU', franking: country === 'AU', gstBas: country === 'AU', superannuation: country === 'AU', vatReturn: country === 'GB',
});

/** Country-specific features for the current entity (API `entity.features`): hide AU-only screens elsewhere. */
export const useFeatures = (): EntityFeatures => {
  const trpc = useTRPC();
  const { entityId, entity } = useSession();
  const q = useQuery(trpc.entities.get.queryOptions(entityId ? { entityId } : skipToken, { meta: { silent: true }, staleTime: 5 * 60_000 }));
  return q.data?.features ?? featuresForCountry(entity?.country);
};

/**
 * The current entity's plan (`entities.get`). `allows` is true until the plan is known, so nothing flashes locked;
 * the server still enforces every gate.
 */
export const usePlan = () => {
  const trpc = useTRPC();
  const { entityId, status } = useSession();
  const q = useQuery(trpc.entities.get.queryOptions(entityId ? { entityId } : skipToken, { meta: { silent: true }, staleTime: 5 * 60_000 }));
  const plan = q.data?.plan ?? null;
  return { plan, pending: status === 'loading' || (Boolean(entityId) && q.isPending), allows: (f: PlanFeature) => plan === null || planAllows(plan, f) };
};
